import React, { useState } from 'react';
import Icon from './UI/Icon';
import './Treatments.css';

const treatmentsData = [
  { name: 'Cervical Spondylitis', category: 'spine', desc: 'Therapies to relieve cervical stiffness, cervical nerve decompression, and restore range of neck movement.' },
  { name: 'Frozen Shoulder', category: 'joints', desc: 'Gentle, guided mobilization treatments and active stretches to restore full scapular mobility.' },
  { name: 'Osteoarthritis (OA)', category: 'joints', desc: 'Strength-building, joint preservation exercises, and pain management to prevent further joint cartilage wear.' },
  { name: 'Rheumatoid Arthritis (RA)', category: 'joints', desc: 'Holistic clinical management during flare-ups and tailored programs to maintain functional independence.' },
  { name: 'Low Back Pain', category: 'spine', desc: 'Core stabilizing exercises, manual lumbar therapies, and spinal posture alignment for back pain relief.' },
  { name: 'Sciatica', category: 'spine', desc: 'Targeted nerve flossing, lumbar spine decompression, and stretching to eliminate radiating leg pain.' },
  { name: 'Sacroiliitis', category: 'spine', desc: 'Pelvic alignment corrections, SI joint stabilization, and local muscle release to relieve lower hip pain.' },
  { name: 'Tail Bone Pain (Coccydynia)', category: 'spine', desc: 'Focused coccyx mobilization, customized seat advice, and pelvic floor muscle relaxation therapies.' },
  { name: 'Golfer’s Elbow', category: 'sports', desc: 'Tendon conditioning, forearm muscle friction release, and strengthening to resolve inner elbow pain.' },
  { name: 'Tennis Elbow', category: 'sports', desc: 'Eccentric wrist exercises, wrist brace consultations, and forearm tendon therapy for outer elbow relief.' },
  { name: 'Total Knee Replacement Rehab', category: 'surgery', desc: 'Post-operative mobilization protocols, muscle activation, and gait retraining to regain independent walking.' },
  { name: 'Hip Replacement Rehab', category: 'surgery', desc: 'Post-surgery hip joint stabilization, muscle strengthening, and gait re-education to restore daily activity.' },
  { name: 'ACL Reconstruction Rehab', category: 'surgery', desc: 'Structured ligament graft protection, range-of-motion recovery, and sports agility conditioning.' },
  { name: 'PCL / MCL / LCL Rehab', category: 'surgery', desc: 'Graduated weight-bearing protocols, side-to-side knee stability conditioning, and muscular balance exercises.' },
  { name: 'Spinal Surgery Rehab', category: 'surgery', desc: 'Careful post-decompression / fusion mobilization, core muscle building, and safe biomechanical posture training.' }
];

const categories = [
  { id: 'all', label: 'All Treatments' },
  { id: 'spine', label: 'Spine & Nerves' },
  { id: 'joints', label: 'Joints & Bones' },
  { id: 'sports', label: 'Sports & Muscles' },
  { id: 'surgery', label: 'Post-Surgery Rehab' }
];

const getTreatmentImage = (treat) => {
  const name = treat.name.toLowerCase();

  if (name.includes('cervical')) return encodeURI('/assets/Cervical spondylitis overview and symptoms.png');
  if (name.includes('frozen shoulder')) return '/assets/frozenSolder.jpg';
  if (name.includes('osteoarthritis')) return encodeURI('/assets/Osteoarthritis (OA).jpg');
  if (name.includes('rheumatoid')) return encodeURI('/assets/Rheumatoid Arthritis (RA).jpg');
  if (name.includes('low back')) return encodeURI('/assets/Low Back Pain.jpg');
  if (name.includes('sciatica')) return '/assets/Sciatica.jpg';
  if (name.includes('sacroiliitis')) return '/assets/Sacroiliitis.jpg';
  if (name.includes('tail bone') || name.includes('coccydynia')) return encodeURI('/assets/Tail Bone Pain (Coccydynia).jpg');
  if (name.includes('golfer') || name.includes('tennis')) return '/assets/ser3.jpeg';
  if (name.includes('total knee replacement')) return '/assets/Total Knee Replacement Rehab.jpg';
  if (name.includes('hip replacement')) return '/assets/ser5.jpg';
  if (name.includes('acl reconstruction')) return '/assets/ACL Reconstruction Rehab.jpg';
  if (name.includes('pcl') || name.includes('mcl') || name.includes('lcl')) return '/assets/PCL.jpg';
  if (name.includes('spinal surgery')) return '/assets/Spinal Surgery Rehab.jpg';

  if (treat.category === 'surgery') return '/assets/ser5.jpg';

  return '/assets/ser1.jpeg';
};

const Treatments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTreatments = treatmentsData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.desc.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <section id="services" className="section bg-white">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <span className="section-tag">Clinical Catalog</span>
          <h2 className="section-title">Conditions & Treatments</h2>
          <p className="section-subtitle">
            Search for your symptoms or filter by clinical category to explore our holistic, evidence-based physiotherapy services.
          </p>
        </div>

        {/* Filter and Search Controls Block */}
        <div className="catalog-controls glass-card">
          
          {/* Active Search Input */}
          <div className="search-box">
            <div className="search-icon-wrapper">
              <Icon name="search" size={20} />
            </div>
            <input 
              type="text" 
              placeholder="Search e.g. Sciatica, Frozen Shoulder, Knee Replacement..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <Icon name="x" size={16} />
              </button>
            )}
          </div>

          {/* Categories Tab Bar */}
          <div className="category-tabs">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

        </div>

        {/* Dynamic Condition Results Grid */}
        {filteredTreatments.length > 0 ? (
          <div className="grid-3 treatments-grid animate-fade-in-up">
            {filteredTreatments.map((treat, idx) => (
              <div key={idx} className="treatment-card glass-card">
                <img
                  src={getTreatmentImage(treat)}
                  alt={treat.name}
                  className="treatment-image"
                />
                <div className="treatment-card-header">
                  <div className="treatment-category-badge">
                    {treat.category === 'spine' && <Icon name="shield" size={12} />}
                    {treat.category === 'joints' && <Icon name="activity" size={12} />}
                    {treat.category === 'sports' && <Icon name="award" size={12} />}
                    {treat.category === 'surgery' && <Icon name="checkcircle" size={12} />}
                    <span>{categories.find(c => c.id === treat.category)?.label}</span>
                  </div>
                </div>
                
                <h3 className="treatment-title">{treat.name}</h3>
                <p className="treatment-desc">{treat.desc}</p>
                
                <a 
                  href={`https://wa.me/918826265224?text=Hello%20Dr.%20Anil,%20I%20want%20to%20consult%20for%20${encodeURIComponent(treat.name)}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="treatment-link-btn"
                >
                  <span>Request Therapy Details</span>
                  <Icon name="chevronright" size={14} />
                </a>
              </div>
            ))}
          </div>
        ) : (
          /* Empty Search Fallback */
          <div className="empty-results-box glass-card animate-fade-in-up">
            <div className="empty-icon-wrapper">
              <Icon name="search" size={48} />
            </div>
            <h3>No Treatments Found</h3>
            <p>We couldn't find any results matching "{searchTerm}". Try checking your spelling or filter another category.</p>
            <button className="btn btn-outline" onClick={() => { setSearchTerm(''); setActiveCategory('all'); }}>
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Treatments;
